import React from "react";
import { Box, Typography } from "@mui/material";
import { zxcvbn, zxcvbnOptions } from "@zxcvbn-ts/core";
import * as zxcvbnCommonPackage from "@zxcvbn-ts/language-common";
import * as zxcvbnEnPackage from "@zxcvbn-ts/language-en";
import { MyCheckCircleIcon, MyRadioButtonUncheckedIcon } from "../../Assets/svg/MyIcons";
import { FRONTEND_MESSAGES } from "../../utils/messages";

const { PASSWORD_RULES } = FRONTEND_MESSAGES;

const options = {
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary,
  },
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
  translations: zxcvbnEnPackage.translations,
};
zxcvbnOptions.setOptions(options);

const rules = [
  { label: PASSWORD_RULES.RULES.LENGTH, test: (p) => p.length >= 8 && p.length <= 20 },
  { label: PASSWORD_RULES.RULES.UPPERCASE, test: (p) => /[A-Z]/.test(p) },
  { label: PASSWORD_RULES.RULES.LOWERCASE, test: (p) => /[a-z]/.test(p) },
  { label: PASSWORD_RULES.RULES.NUMBER, test: (p) => /[0-9]/.test(p) },
  { label: PASSWORD_RULES.RULES.SPECIAL_CHAR, test: (p) => /[!"#$%&'()*+,\-./:;<=>?@\[\\\]\^_`{|}~]/.test(p) },
  { label: PASSWORD_RULES.RULES.NO_SPACES, test: (p) => !/\s/.test(p) },
];

export function isPasswordValid(password) {
  return rules.every((r) => r.test(password));
}

export function getPasswordScore(password) {
  if (!password) return 0;
  const result = zxcvbn(password);
  return result.score;
}

function getStrengthLabel(score) {
  switch (score) {
    case 0: return PASSWORD_RULES.STRENGTH.VERY_WEAK;
    case 1: return PASSWORD_RULES.STRENGTH.WEAK;
    case 2: return PASSWORD_RULES.STRENGTH.FAIR;
    case 3: return PASSWORD_RULES.STRENGTH.STRONG;
    case 4: return PASSWORD_RULES.STRENGTH.VERY_STRONG;
    default: return PASSWORD_RULES.STRENGTH.VERY_WEAK;
  }
}

function getScoreColor(score) {
  if (score <= 1) return "#d32f2f";
  if (score === 2) return "#ed6c02";
  if (score === 3) return "#fbc02d";
  return "#2e7d32";
}

function getScoreBgColor(score) {
  if (score <= 1) return "rgba(211, 47, 47, 0.1)";
  if (score === 2) return "rgba(237, 108, 2, 0.1)";
  if (score === 3) return "rgba(251, 192, 45, 0.1)";
  return "rgba(46, 125, 50, 0.1)";
}

export default function PasswordStrengthRules({ password, show = true, inline = false }) {
  if (!password || !show) return null;

  const result = zxcvbn(password);
  const score = result.score;
  const allRulesPassed = rules.every((r) => r.test(password));
  const rulesPassedCount = rules.filter((r) => r.test(password)).length;

  const displayScore = allRulesPassed ? Math.max(score, 3) : Math.min(score, Math.floor((rulesPassedCount / rules.length) * 4));

  return (
    <Box sx={{ position: 'relative' }}>
      {!inline && (
        <Box
          sx={{
            position: "absolute",
            top: '4rem',
            left: -6,
            width: 12,
            height: 12,
            transform: "rotate(45deg)",
            backgroundColor: "#f5f5f9",
            borderLeft: "1px solid #dadde9",
            borderBottom: "1px solid #dadde9",
            zIndex: 1,
          }}
        />
      )}
      <Box
        sx={{
          borderRadius: inline ? "10px" : 2,
          border: inline ? "1px solid #e2e7f0" : "1px solid #dadde9",
          backgroundColor: inline ? "#f8fafc" : "#f5f5f9",
          boxShadow: inline
            ? "none"
            : "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          p: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "rgba(0, 0, 0, 0.87)" }}>
            Password Requirements
          </Typography>
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              color: getScoreColor(displayScore),
              backgroundColor: getScoreBgColor(displayScore),
            }}
          >
            {getStrengthLabel(displayScore)}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 0.5, mb: 2 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                height: 4,
                flex: 1,
                borderRadius: 1,
                transition: "background-color 0.3s",
                backgroundColor: i <= displayScore ? getScoreColor(displayScore) : "#e0e0e0",
              }}
            />
          ))}
        </Box>

        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 1 }}>
          {rules.map((rule) => {
            const passed = rule.test(password);
            return (
              <Box key={rule.label} component="li" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {passed ? (
                  <MyCheckCircleIcon />
                ) : (
                  <MyRadioButtonUncheckedIcon />
                )}
                <Typography
                  sx={{
                    fontSize: "11px",
                    lineHeight: 1.4,
                    transition: "color 0.2s",
                    color: passed ? "rgba(0, 0, 0, 0.87)" : "rgba(0, 0, 0, 0.6)",
                    fontWeight: passed ? 500 : 400,
                  }}
                >
                  {rule.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {result.feedback.warning && (
          <Typography sx={{ mt: 1.5, fontSize: "11px", color: "#d32f2f" }}>
            {result.feedback.warning}
          </Typography>
        )}
        {result.feedback.suggestions.length > 0 && (
          <Typography sx={{ mt: 0.5, fontSize: "11px", color: "rgba(0, 0, 0, 0.6)" }}>
            {result.feedback.suggestions[0]}
          </Typography>
        )}
      </Box>
    </Box>
  );
}